import React, { useRef, useState } from "react";
import { Form, Button, Row, Col, FormLabel, Spinner, Modal } from "react-bootstrap";
import SignatureCanvas from "react-signature-canvas";
import { useFormik } from "formik";
import * as Api from "../../General/api";
import * as Common from "../../General/common";
import { apiUrls } from "../../constants/endpoints";

const ElectronicAOBSigning = ({
  show,
  facilityName,
  selectedResidents,
  onBack,
  onSignedSuccess,
  onClose,
}) => {
  const signatureRef = useRef(null);
  const [isAuthorizedConfirm, setIsAuthorizedConfirm] = useState(false);
  const [representativeName, setRepresentativeName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const todayDate = new Date().toLocaleDateString();

  const formik = useFormik({
    initialValues: {
      isSelectAll: false,
      isPhysicallyAble: false,
      isPatientRights: false,
      isPatientResponsibilities: false,
      isFinancialResponsibility: false,
      isAssignmentRelease: false,
      isCompanyInformation: false,
      isTelephoneNumber: false,
      isWarrantyInformation: false,
      isPracticesNotice: false,
      isCopyOfSupplier: false,
      physicallyAbleTextArea: "",
    },
    onSubmit: () => {},
  });

  const toggle = (field) => {
    formik.setFieldValue(field, !formik.values[field]);
  };

  const rowClass = "custom-grey-background p-1 mb-1 rounded mx-0";
  const checkStyle = { marginLeft: "-28px", fontSize: "0.82rem" };
  const normalTextStyle = { fontSize: "0.82rem", lineHeight: "1.25" };

  const renderCheckboxRow = (field, label) => (
    <Row className={rowClass}>
      <Col xs={12}>
        <div className="form-check form-check-inline custom-grey-background rounded">
          <Form.Check
            style={checkStyle}
            checked={formik.values[field]}
            onChange={() => toggle(field)}
            type="checkbox"
            id={`aob-${field}`}
            name={`aob-${field}`}
            label={label}
          />
          <FormLabel className="form-check-label" htmlFor={`aob-${field}`}>
            <span className="switch-icon-left"></span>
            <span className="switch-icon-right"></span>
          </FormLabel>
        </div>
      </Col>
    </Row>
  );

  const handleClearSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
  };

  const handleGenerateAOBs = async () => {
    if (!isAuthorizedConfirm) {
      Common.showSuccessAlert(
        "Please confirm authorization before signing",
        "warning",
        "Confirmation Required",
        3000
      );
      return;
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      Common.showSuccessAlert(
        "Please provide a signature",
        "warning",
        "Signature Required",
        3000
      );
      return;
    }

    if (!representativeName.trim()) {
      Common.showSuccessAlert(
        "Please enter representative name",
        "warning",
        "Name Required",
        3000
      );
      return;
    }

    try {
      setIsGenerating(true);

      const signatureBase64 = signatureRef.current.toDataURL();
      const shared = {
        todayDate,
        isPhysicallyAble: formik.values.isPhysicallyAble,
        isPatientRights: formik.values.isPatientRights,
        isPatientResponsibilities: formik.values.isPatientResponsibilities,
        isFinancialResponsibility: formik.values.isFinancialResponsibility,
        isAssignmentRelease: formik.values.isAssignmentRelease,
        isCompanyInformation: formik.values.isCompanyInformation,
        isTelephoneNumber: formik.values.isTelephoneNumber,
        isWarrantyInformation: formik.values.isWarrantyInformation,
        isPracticesNotice: formik.values.isPracticesNotice,
        isCopyOfSupplier: formik.values.isCopyOfSupplier,
        reasonUnableToSign: (formik.values.physicallyAbleTextArea ?? "").trim(),
        representativeName: representativeName.trim(),
        representativeTitle: "",
        isAobWithFacility: true,
      };
      
      const items = selectedResidents.map((resident) => {
        const residentName = [
          Common.replaceNullWithString(resident.lastName),
          Common.replaceNullWithString(resident.firstName),
        ].filter(Boolean).join(", ") || "";
        return {
          residentId: resident.id,
          residentName,
          facilityName: Common.replaceNullWithString(resident.facilityName || facilityName || ""),
          medicareId: resident.medicare ?? resident.medicareId ?? "",
          primaryInsurance: resident.primaryInsurance ?? "",
          secondaryInsurance: resident.secondaryInsurance ?? "",
          ...shared,
        };
      });
      
      const payload = {
        signatureImageBase64: signatureBase64,
        items,
      };

      const response = await Api.postRequest(apiUrls.generateAobPdf, payload, false);

      if (response.data.success === true) {
        Common.showSuccessAlert(
          response.data.message || `Successfully generated ${selectedResidents.length} AOB PDF(s)`,
          "success",
          "Success",
          3000
        );
        setIsAuthorizedConfirm(false);
        setRepresentativeName("");
        formik.resetForm();
        if (signatureRef.current) signatureRef.current.clear();
        if (onSignedSuccess) onSignedSuccess();
        if (onBack) onBack();
      } else {
        Common.showSuccessAlert(
          response.data.message || "Failed to generate AOB PDFs",
          "error",
          "Error",
          3000
        );
      }
    } catch (error) {
      console.error("Error generating AOB PDFs:", error);
      Common.showSuccessAlert(
        "An error occurred while generating AOB PDFs",
        "error",
        "Error",
        3000
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal
      size="lg"
      centered
      scrollable
      backdrop="static"
      keyboard={false}
      show={show}
      onHide={onClose}
    >
      <Modal.Header className="py-2 position-relative">
        <div className="w-100 pr-4">
          <span className="align-middle" style={{ fontSize: "1.2rem", lineHeight: "1.1" }}>AOB Management</span>
          <div className="mt-1" style={{ fontSize: "0.85rem", lineHeight: "1.35" }}>
            <div>View and manage Assignment of Benefits (AOB) documents for Medicare residents.</div>
          </div>
          <h4 className="text-center mt-2 mb-0">Electronic AOB Signing</h4>
        </div>
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={onClose}
          style={{ position: "absolute", top: "16px", right: "12px" }}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>

      <Modal.Body className="py-2">
        <div id="pdf-container">
          <div style={{ fontSize: "0.88rem", marginBottom: "0.5rem", lineHeight: "1.25" }}>
            <div>Facility: {facilityName || ""}</div>
            <div>Resident(s) Selected: {selectedResidents.map((r) => Common.replaceNullWithString(r.firstName)).filter(Boolean).join(", ") || "—"}</div>
            <div>One signature will be applied to an individual AOBs for each residents listed above.</div>
          </div>

          <div className="alert alert-warning py-2 mb-2" role="alert">
            This is a AOB template. Resident-specific information will be applied automatically.
          </div>

          <div className="border p-2" style={{ maxHeight: "200px", overflowY: "auto", overflowX: "hidden" }}>
            <h5 className="text-center mb-2" style={{ fontSize: "1rem" }}>Assignment of Benefits (AOB)</h5>
            <p className="mb-2 fw-bold" style={{ fontSize: "0.8rem" }}>
              My check mark adjacent to the following items, along with my signature below, attest that I have received, read, and/or been instructed, in detail, on this information.
            </p>
            <Row className={rowClass} id="select-all">
              <Col xs={12}>
                <div className="form-check form-check-inline custom-grey-background rounded">
                  <Form.Check
                    style={checkStyle}
                    checked={formik.values.isSelectAll}
                    onChange={() => {
                      const checked = !formik.values.isSelectAll;
                      formik.setFieldValue("isSelectAll", checked);
                      formik.setFieldValue("isPatientRights", checked);
                      formik.setFieldValue("isPatientResponsibilities", checked);
                      formik.setFieldValue("isFinancialResponsibility", checked);
                      formik.setFieldValue("isAssignmentRelease", checked);
                      formik.setFieldValue("isCompanyInformation", checked);
                      formik.setFieldValue("isTelephoneNumber", checked);
                      formik.setFieldValue("isWarrantyInformation", checked);
                      formik.setFieldValue("isPracticesNotice", checked);
                      formik.setFieldValue("isCopyOfSupplier", checked);
                    }}
                    type="checkbox"
                    id="aob-select-all"
                    name="aob-select-all"
                    label="Click to select all boxes below"
                  />
                  <FormLabel className="form-check-label" htmlFor="aob-select-all">
                    <span className="switch-icon-left"></span>
                    <span className="switch-icon-right"></span>
                  </FormLabel>
                </div>
              </Col>
            </Row>

            {renderCheckboxRow("isPatientRights", "My rights as a patient/client (cp)")}
            {renderCheckboxRow("isPatientResponsibilities", "My responsibilities as a patient/client (cp)")}
            {renderCheckboxRow("isFinancialResponsibility", "My financial responsibility (cp)")}
            {renderCheckboxRow("isAssignmentRelease", "My assignment release (cp)")}
            {renderCheckboxRow("isCompanyInformation", "Information on Company Products/Services (cp)")}
            {renderCheckboxRow("isTelephoneNumber", "Important telephone numbers (cp)")}
            {renderCheckboxRow("isWarrantyInformation", "Warranty Information (cp)")}
            {renderCheckboxRow("isPracticesNotice", "Notice of Privacy Practices")}
            {renderCheckboxRow("isCopyOfSupplier", "Copy of supplier standards")}

            <Row className="mx-0">
              <Col xs={12}>
                <div className="mt-2" style={normalTextStyle}>
                  <ol>
                    <li>
                      Assignment of Medicare, Medicaid, Medicare Supplemental or other insurance benefits to Wound Care Concepts Inc.
                      for medical supplies and/or medication(s) furnished to me by Wound Care Concepts Inc.
                    </li>
                    <li>Direct billing to Medicare, Medicaid, Medicare Supplemental or other insurer(s).</li>
                    <li>
                      Release of my medical information to Medicare, Medicaid, Medicare Supplemental or other insurers and their agents
                      and assigns.
                    </li>
                    <li>
                      Wound Care Concepts Inc. to obtain medical or other information necessary in order to process my claim(s), including
                      determining eligibility and seeking reimbursement for medical supplies and/or medication(s) provided.
                    </li>
                    <li>
                      Wound Care Concepts Inc. to contact me by telephone or mail regarding my medical supplies and/or medication(s)
                      order.
                    </li>
                  </ol>
                  <span>
                    I request that payment of Medicare, Medicaid, Medicare Supplemental or other insurance benefits be made on my behalf
                    to Wound Care Concepts Inc. for any medical supplies and/or medications furnished to me by Wound Care Concepts Inc..
                    I authorize any holder of medical information about me to release to Wound Care Concepts Inc., my physician(s),
                    caregiver, CMS, its agents and to my primary and/or other medical insurer any information needed to determine or
                    secure eligibility information and/or reimbursement for covered services. I agree to pay all amounts that are not
                    covered by my insurer(s) and for which I am responsible.
                  </span>
                </div>
              </Col>
            </Row>

            {renderCheckboxRow("isPhysicallyAble", "Is the Patient physically and mentally able to sign this form?")}

            {!formik.values.isPhysicallyAble && (
              <Row className="mt-2 mx-0">
                <Col xs={12}>
                  <Form.Group controlId="physicallyAbleTextArea" className="mb-0">
                    <Form.Label style={normalTextStyle}>Reason why patient is unable to sign</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formik.values.physicallyAbleTextArea || ""}
                      onChange={(e) => formik.setFieldValue("physicallyAbleTextArea", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </div>

          <div className="mt-2 border-top pt-2">
            <div className="d-flex justify-content-center">
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "fit-content", marginRight: "72px" }}>
                <h5 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Authorized Signature</h5>
                <div className="d-flex flex-wrap align-items-stretch" style={{ gap: "48px" }}>
                  <div className="d-flex align-items-stretch" style={{ gap: "4px", flexShrink: 0 }}>
                    <div
                      className="border rounded p-2 bg-white"
                      style={{ width: "300px", display: "flex", justifyContent: "flex-start" }}
                    >
                      <div style={{ width: 260, height: 90, flexShrink: 0 }}>
                        <SignatureCanvas
                          ref={signatureRef}
                          penColor="green"
                          canvasProps={{
                            width: 260,
                            height: 90,
                            className: "signature-canvas",
                            style: { display: "block", width: 260, height: 90 },
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className="d-flex flex-column justify-content-end"
                      style={{ minHeight: "103px" }}
                    >
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleClearSignature}
                        disabled={isGenerating}
                        className="py-0 px-2"
                        style={{ fontSize: "0.875rem", lineHeight: 1.2 }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div style={{ minWidth: "250px" }}>
                    <Form.Group className="mb-2" style={{ marginTop: 0 }}>
                      <Form.Label className="mb-1" style={{ fontSize: "0.9rem" }}>Date</Form.Label>
                      <Form.Control type="text" value={todayDate} readOnly />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1" style={{ fontSize: "0.9rem" }}>Print Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={representativeName}
                        onChange={(e) => setRepresentativeName(e.target.value)}
                        disabled={isGenerating}
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-check d-flex align-items-center" style={{ minHeight: "1.25rem", marginTop: "10px" }}>
              <input
                type="checkbox"
                id="authorized-confirm"
                className="form-check-input mt-0"
                checked={isAuthorizedConfirm}
                onChange={(e) => setIsAuthorizedConfirm(e.target.checked)}
              />
              <label className="form-check-label mb-0" htmlFor="authorized-confirm" style={{ lineHeight: "1.25rem" }}>
                I confirm I am authorized to sign on behalf of the resident(s).
              </label>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="py-2 d-flex align-items-center">
        <Button
          variant="link"
          className="btn-sm p-0 mr-auto"
          onClick={onBack}
          disabled={isGenerating}
        >
          Back to AOB Management
        </Button>
        <Button
          variant="primary"
          className="btn-sm mr-2"
          onClick={handleGenerateAOBs}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mr-1" />
              <span className="align-middle"> Generating...</span>
            </>
          ) : (
            <span className="align-middle">Confirm &amp; Sign</span>
          )}
        </Button>
        <Button variant="danger" className="btn-sm" onClick={onClose}>
          <span className="align-middle">Close</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ElectronicAOBSigning;
